/**
 * Eufy 体組成データ用 Zustand ストア
 *
 * フルアクセス権限を持つユーザーのみ使用可能
 */

import { create } from 'zustand'

import {
  eufyLogin,
  eufyLogout,
  eufySelectCustomer,
  getEufyMeasurements,
  getEufyStatus,
  getEufySummary,
} from '@/api/services/eufy'
import type { EufyLoginRequest, EufyMeasurementItem, EufyStatusResponse, EufySummaryResponse } from '@/api/types/eufy'

type EufyState = {
  status: EufyStatusResponse | null
  isStatusLoading: boolean

  measurements: EufyMeasurementItem[]
  isMeasurementsLoading: boolean

  summary: EufySummaryResponse | null
  isSummaryLoading: boolean

  error: string | null

  // 認証
  checkStatus: () => Promise<void>
  login: (body: EufyLoginRequest) => Promise<void>
  selectCustomer: (customerId: string) => Promise<void>
  logout: () => Promise<void>

  // データ取得
  fetchMeasurements: (params?: { customer_id?: string; start?: string; end?: string; limit?: number }) => Promise<void>
  fetchSummary: (customerId?: string) => Promise<void>

  clearError: () => void
  reset: () => void
}

const initialState = {
  status: null,
  isStatusLoading: false,
  measurements: [],
  isMeasurementsLoading: false,
  summary: null,
  isSummaryLoading: false,
  error: null,
}

export const useEufyStore = create<EufyState>((set) => ({
  ...initialState,

  checkStatus: async () => {
    set({ isStatusLoading: true, error: null })
    try {
      const status = await getEufyStatus()
      set({ status })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Eufyステータスの取得に失敗しました'
      set({ error: message })
    } finally {
      set({ isStatusLoading: false })
    }
  },

  login: async (body) => {
    set({ isStatusLoading: true, error: null })
    try {
      await eufyLogin(body)
      // ログイン後の状態を取りなおす
      const status = await getEufyStatus()
      set({ status })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Eufyログインに失敗しました'
      set({ error: message })
      throw error
    } finally {
      set({ isStatusLoading: false })
    }
  },

  selectCustomer: async (customerId) => {
    set({ isStatusLoading: true, error: null })
    try {
      const status = await eufySelectCustomer(customerId)
      set({ status })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'メンバー選択に失敗しました'
      set({ error: message })
    } finally {
      set({ isStatusLoading: false })
    }
  },

  logout: async () => {
    set({ isStatusLoading: true, error: null })
    try {
      await eufyLogout()
      set({ status: null, measurements: [], summary: null })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'ログアウトに失敗しました'
      set({ error: message })
    } finally {
      set({ isStatusLoading: false })
    }
  },

  fetchMeasurements: async (params = {}) => {
    set({ isMeasurementsLoading: true, error: null })
    try {
      const res = await getEufyMeasurements(params)
      set({ measurements: res.items })
    } catch (error) {
      const message = error instanceof Error ? error.message : '体組成データの取得に失敗しました'
      set({ error: message })
    } finally {
      set({ isMeasurementsLoading: false })
    }
  },

  fetchSummary: async (customerId) => {
    set({ isSummaryLoading: true, error: null })
    try {
      const summary = await getEufySummary(customerId)
      set({ summary })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'サマリの取得に失敗しました'
      set({ error: message })
    } finally {
      set({ isSummaryLoading: false })
    }
  },

  clearError: () => set({ error: null }),
  reset: () => set(initialState),
}))
